import _ from 'lodash';

import { CONFIG } from 'constants/index';
import { ValidationResult } from './validation';

interface RequestParams {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
  body?: Record<string, unknown> | void;
  secure?: boolean;
}

export type ApiResponse<R, E> = ({ success: true } & R) | ({ success: false } & E);

/**
 * Http request helper
 */
export const api = {
  async request<R, E>(params: RequestParams, target: 'admin' | 'client' | 'client-v2') {
    const apiBaseUrlMap = {
      client: CONFIG.api,
      ['client-v2']: CONFIG.apiv2,
      admin: CONFIG.adminApi,
    };
    const { url, method, body } = params;
    const query = method === 'GET' && !_.isEmpty(body) ? `?${urlParams(body)}` : '';
    const apiBaseUrl = apiBaseUrlMap[target];
    const targetUrl = `${apiBaseUrl}${url}${query}`;
    const headers = { 'Content-Type': 'application/json; charset=utf-8', ...(params.headers || {}) };
    const options: RequestInit = { mode: 'cors', credentials: 'include' };

    options.method = method;
    options.headers = headers;
    if (method !== 'GET') options.body = JSON.stringify(body);

    const response = await fetch(targetUrl, options);
    const jsonResp = await response.json();
    const result: ApiResponse<R, E> = {
      ...jsonResp,
    };

    return result;
  },
};

export const urlParams = (obj: RequestParams['body']) => {
  if (obj) {
    return Object.entries(obj)
      .map(([key, val]) => `${key}=${val}`)
      .join('&');
  }
  return '';
};

/**
 * Endpoint wrap helper
 */
export type Endpoint<Params extends Record<string, unknown> | void, Result, Error> = (
  params: Params,
  headers?: Record<string, string>,
) => {
  args: {
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    url: string;
    body?: Params;
    secure?: boolean;
  };
  invoke: (target?: 'admin' | 'client' | 'client-v2') => Promise<ApiResponse<Result, Error>>;
};

export const endpoint = <Params extends Record<string, unknown> | void, Result, Error>(fetchArgs: {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url: (params: Params) => string;
}): Endpoint<Params, Result, Error> => {
  return (params, headers) => ({
    args: {
      method: fetchArgs.method,
      headers,
      url: `${fetchArgs.url(params)}${fetchArgs.method === 'GET' ? '?' + urlParams(params) : ''}`,
      body: fetchArgs.method !== 'GET' ? params : undefined,
    },
    invoke(target = 'client') {
      return api.request(this.args, target);
    },
  });
};

/**
 * Endpoint errors list
 */
export enum ERROR {
  INVALID_DATA = 'INVALID_DATA',
  ACCESS_DENIED = 'ACCESS_DENIED',
  UNEXPECTED_ERROR = 'UNEXPECTED_ERROR',
  UNAUTHORIZED_REQUEST = 'UNAUTHORIZED_REQUEST',
  ITEM_ALREADY_EXISTS = 'ITEM_ALREADY_EXISTS',
}

/**
 * Common error type
 */
export type EndpointError =
  | {
      code: ERROR.INVALID_DATA;
      validation: ValidationResult;
    }
  | {
      code: ERROR.ACCESS_DENIED;
    }
  | {
      code: ERROR.UNEXPECTED_ERROR | ERROR.ITEM_ALREADY_EXISTS;
      details: string;
    };
